import { Req, Res } from '@cbo/shared-library';
import Autocomplete from '@mui/material/Autocomplete';
import Grid, { GridProps } from '@mui/material/Grid';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import { useQueryClient } from '@tanstack/react-query';
import Ty from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { useMemo, useState } from 'react';
import { Controller, ControllerRenderProps, FieldValues, useForm } from 'react-hook-form';
import { FormContainer } from 'react-hook-form-mui';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { useHistory } from 'react-router-dom';
import createFormFields from '../../../components/FormTextFields/FormTextFields';
import Header from '../../../components/Header/Header';
import { useCompanyLinks } from '../../../contexts/companyLinksContext';
import { useSnackbar } from '../../../contexts/SnackbarContext';
import { TViewersSetting } from '../../../models/config/viewers';
import useYupValidationResolver from '../../../utils/formUtils/yupValidationResolver';
import { useCompanyLinkCreateMutation, useCompanyLinkUpdateMutation } from '../../requests/mutations';
import '../../../../App.css';
import queryKeys from '../../requests/queryKeys';
import { useUsers } from '../../../contexts/userContext';
import UnsavedFormDataPrompt from '../../../components/UnsavedDataPrompt/UnsavedFormDataPrompt';
import FormFooter from '../../../components/StickyFooter/FormFooter';

type TLinkForm = {
  linkUrl: string;
  linkText: string;
  viewers: TViewersSetting[];
};

type TCompanyLinkFields = {
  name: 'linkUrl' | 'linkText' | 'viewers';
  required?: boolean;
  dataTestId: string;
  gridProps?: GridProps;
};

const companyLinkFields: TCompanyLinkFields[] = [
  {
    name: 'linkUrl',
    dataTestId: 'linkUrl-textfield',
    gridProps: {
      xs: 12,
      sm: 6,
      md: 4,
      lg: 3,
    },
  },
  {
    name: 'linkText',
    dataTestId: 'linkText-textfield',
    gridProps: {
      xs: 12,
      sm: 6,
      md: 4,
      lg: 3,
    },
  },
  {
    name: 'viewers',
    dataTestId: 'viewers-autocomplete-multi-select',
    gridProps: {
      xs: 12,
      sm: 6,
      md: 4,
      lg: 3,
    },
  },
];

interface CreateEditCompanyLinkFormProps {
  setIsCreatingOrEditingLink: (isCreatingOrEditingLink: boolean) => void;
  areRolesLoading: boolean | null;
  viewers: TViewersSetting[];
  // eslint-disable-next-line react/require-default-props
  linkToEdit?: Res.Firebase.Link;
}

function CreateEditCompanyLinkForm({
  setIsCreatingOrEditingLink,
  areRolesLoading,
  viewers,
  linkToEdit,
}: CreateEditCompanyLinkFormProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isScreenSmallOrLess = useMediaQuery(theme.breakpoints.down('sm'));
  const { setSnackbarState } = useSnackbar();
  const { setShouldRefetchLinks } = useCompanyLinks();
  const editLinkMutation = useCompanyLinkUpdateMutation();
  const addLinkMutation = useCompanyLinkCreateMutation();
  const everyoneRoleIds: string[] = viewers.map((viewer) => viewer.id).slice(1);
  const queryClient = useQueryClient();
  const { org } = useUsers();

  const [attemptedCancelWhileDirty, setAttemptedCancelWhileDirty] = useState(false);

  const afterSaveSuccess = () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.companyLinks.sideBarCompanyLinks(org?.bslId) });
    setShouldRefetchLinks(true);
    setIsCreatingOrEditingLink(false);
    setSnackbarState({
      open: true,
      message: t('admin.editLinksPage.linkSavedMessage'),
      color: 'success',
    });
    const allCompanyLinks = queryKeys.companyLinks.companyLinks(org?.bslId);
    queryClient.removeQueries({ queryKey: allCompanyLinks });
  };

  const save = async (link: TLinkForm) => {
    let readRoleIds: string[] = link.viewers.map((viewer) => viewer.id);

    if (readRoleIds.length === 1 && !!readRoleIds.find((id) => id === 'Everyone')) {
      readRoleIds = everyoneRoleIds;
    }

    try {
      if (linkToEdit) {
        const editedLink: Req.Firebase.Link = {
          id: linkToEdit.id,
          displayName: link.linkText,
          url: link.linkUrl,
          readRoleIds,
        };

        editLinkMutation.mutate(
          { linkToEdit: editedLink },
          {
            onSuccess: () => {
              afterSaveSuccess();
            },
            onError: () => {
              setSnackbarState({ open: true, message: t('admin.editLinksPage.linkEditErrorMessage'), color: 'error' });
            },
          }
        );
      } else {
        const newLink: Req.Firebase.LinkData = {
          displayName: link.linkText,
          url: link.linkUrl,
          readRoleIds,
        };

        addLinkMutation.mutate(
          { newLink },
          {
            onSuccess: () => {
              afterSaveSuccess();
            },
            onError: () => {
              setSnackbarState({
                open: true,
                message: t('admin.editLinksPage.linkCreationErrorMessage'),
                color: 'error',
              });
            },
          }
        );
      }
    } catch (error) {
      setSnackbarState({ open: true, message: t('admin.editLinksPage.linkEditErrorMessage'), color: 'error' });
    }
  };

  function handleAutocompleteChange(
    event: React.SyntheticEvent,
    data: TViewersSetting[],
    field: ControllerRenderProps<FieldValues, 'linkUrl' | 'linkText' | 'viewers'>
  ) {
    if (!!data.find((viewer) => viewer.id === 'Everyone') && data.length > 1) {
      field.onChange(data.filter((viewer) => viewer.id !== 'Everyone'));
    } else {
      field.onChange(data);
    }
  }

  const companyLinkSchema = useMemo(
    () =>
      yup.object({
        linkUrl: yup
          .string()
          .trim()
          .required(t('formMessage.required'))
          // Matches http:// or https:// - valid url ascii characters - .com, .org, etc - valid post domain characters
          .matches(
            /^https?:\/\/(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-z]{2,4}\b([-a-zA-Z0-9@:%_+.~#?&//=]*)$/,
            t('admin.editLinksPage.linkInvalidErrorMessage')
          ),
        linkText: yup.string().trim().required(t('formMessage.required')),
        viewers: yup.array().min(1, t('formMessage.required')),
      }),
    [t]
  );

  const resolver = useYupValidationResolver(companyLinkSchema);
  const hasReadRoles = (linkToEdit && linkToEdit.readRoles && linkToEdit.readRoles.length > 0) || false;
  const formContext = useForm({
    resolver,
    defaultValues: {
      linkUrl: linkToEdit?.url || '',
      linkText: linkToEdit?.displayName || '',
      viewers: hasReadRoles
        ? linkToEdit?.readRoles?.map((role) => ({ id: role.id, label: role.description }))
        : [{ id: 'Everyone', label: 'Everyone' }],
    },
  });

  const { register, watch, control, formState } = formContext;
  const { isDirty, isSubmitted } = formState;
  const history = useHistory();
  const viewersValue: TViewersSetting[] = watch('viewers');

  const handleCancelClicked = () => {
    if (formState.isDirty) {
      setAttemptedCancelWhileDirty(true);
    } else {
      setIsCreatingOrEditingLink(false);
    }
  };

  return (
    <>
      <UnsavedFormDataPrompt
        open={attemptedCancelWhileDirty}
        onConfirm={() => setIsCreatingOrEditingLink(false)}
        onCancel={() => setAttemptedCancelWhileDirty(false)}
        when={isDirty && !isSubmitted}
        navigate={(path) => history.push(path)}
        shouldBlockNavigation={(location) => isDirty && !isSubmitted}
      />
      <FormContainer onSuccess={(data) => save(data)} formContext={formContext}>
        <Box height='calc(100vh - 100px)'>
          <Box
            sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
            }}
          >
            {/* Form Fields */}
            <Grid
              data-testid='create-company-link-grid-container'
              container
              sx={{
                paddingX: 4,
                paddingY: 2,
                mb: isScreenSmallOrLess ? '150px' : 0,
              }}
            >
              <Grid item container spacing={2}>
                {/* Header */}
                <Grid item xs={12}>
                  <Header
                    dataTestId='create-company-link-title'
                    title={t(
                      linkToEdit
                        ? 'admin.editLinksPage.editCompanyLinksTitle'
                        : 'admin.editLinksPage.createCompanyLinksTitle'
                    )}
                    description={t(
                      linkToEdit
                        ? 'admin.editLinksPage.editCompanyLinksTitleHelper'
                        : 'admin.editLinksPage.createCompanyLinksTitleHelper'
                    )}
                    maxDescriptionWidth='600px'
                  />
                </Grid>

                {/* Link URL */}
                <Grid item container columnSpacing={4}>
                  {createFormFields(companyLinkFields[0], { t, tKey: 'admin.editLinkForm' }, register)}
                  <Grid item xs={12} sm={6} md={8} alignSelf='flex-end' mt={isScreenSmallOrLess ? 1 : 0}>
                    <Ty
                      data-testid='linkUrl-helper'
                      paragraph
                      variant='caption'
                      color='text.secondary'
                      sx={{
                        mb: 0,
                        maxWidth: '600px',
                      }}
                    >
                      {t('admin.editLinkForm.linkUrlHelper')}
                    </Ty>
                  </Grid>
                </Grid>

                {/* Link Text */}
                <Grid item container columnSpacing={4}>
                  {createFormFields(companyLinkFields[1], { t, tKey: 'admin.editLinkForm' }, register)}

                  <Grid item xs={12} sm={6} md={8} alignSelf='flex-end' mt={isScreenSmallOrLess ? 1 : 0}>
                    <Ty
                      data-testid='linkText-helper'
                      paragraph
                      variant='caption'
                      color='text.secondary'
                      sx={{
                        mb: 0,
                        maxWidth: '600px',
                      }}
                    >
                      {t('admin.editLinkForm.linkTextHelper')}
                    </Ty>
                  </Grid>
                </Grid>

                <Grid item container columnSpacing={4}>
                  <Grid item xs={12} sm={6} md={4} lg={3}>
                    <Controller
                      name={companyLinkFields[2].name}
                      control={control}
                      render={({ field, fieldState: { error } }) => (
                        <Autocomplete
                          data-testid='viewers-autocomplete-multi-select'
                          {...field}
                          multiple
                          disableCloseOnSelect
                          getOptionDisabled={(option) =>
                            option.id === 'Everyone' &&
                            viewersValue.length > 0 &&
                            !viewersValue.find((viewer) => viewer.id === 'Everyone')
                          }
                          isOptionEqualToValue={(option, value) => option.id === value.id}
                          options={viewers}
                          getOptionLabel={(option: TViewersSetting) => option.label}
                          loading={!!areRolesLoading}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              variant='standard'
                              label={t('admin.editLinkForm.viewers')}
                              error={!!error}
                              helperText={error ? error.message : ''}
                            />
                          )}
                          onChange={(event, data) => handleAutocompleteChange(event, data, field)}
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={8} alignSelf='flex-end' mt={isScreenSmallOrLess ? 1 : 0}>
                    <Ty
                      data-testid='viewers-helper'
                      paragraph
                      variant='caption'
                      color='text.secondary'
                      sx={{
                        mb: 0,
                        maxWidth: '600px',
                      }}
                    >
                      {t('admin.editLinkForm.viewersHelper')}
                    </Ty>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>

            {/* Buttons */}
            <FormFooter
              buttons={[
                {
                  onClick: handleCancelClicked,
                  dataTestId: 'cancel-button',
                },
                {
                  disabled: !formState.isDirty,
                  dataTestId: 'save-button',
                  submitType: true,
                },
              ]}
            />
          </Box>
        </Box>
      </FormContainer>
    </>
  );
}

export default CreateEditCompanyLinkForm;
